import { ApiError, claimsExpiresAt, parseJwtClaims, unwrap, userFromJwtClaims, userFromProfileSnapshot, type BackendLoginData, type CaptchaResult, type CurrentUser, type LoginPayload, type LoginResult, type ProfileSnapshot } from '@/core';
import { http } from './http';

/**
 * Auth/Identity — 后端文档 §6.0-② + §6.1：`POST /auth/login`、`GET /auth/captcha`、
 * `POST /auth/logout`、`GET /auth/profile`（§6.2-7 缺口）。
 *
 * 本文件是**契约适配边界**：部署版实测 `data = {token, expireMinutes, account}`（不含身份与
 * 权限快照），与前端内部 `LoginResult{accessToken, expiresIn, sessionTimeoutMinutes, user}`
 * 不同名，统一在此归一，业务层零感知。
 */

/**
 * D-1（契约 §2）：会话截止时刻**只认后端给出的三处绝对来源**，优先级
 * ① 响应体 `expiresAt`（t80 新增的绝对时刻，UTC ISO-8601）；② 令牌 `exp` 声明（服务端签名，绝对时刻）；
 * ③ 响应体 `expireMinutes`（后端声明的名义分钟数 ⇒ 换算为绝对时刻）。
 * 三者皆缺 ⇒ **显式失败**（`AUTH_SESSION_DEADLINE_MISSING`）：⛔ 不再按本地常量兜底 30 分钟 ——
 * 那个常量就是「前端自造剩余时长」，也是本次要消灭的口径漂移源。
 */
export interface LoginResultWithDeadline extends LoginResult {
  /** 会话绝对截止时刻（epoch ms）：倒计时与登出判定的唯一取值来源（D-1） */
  sessionExpiresAtMs: number;
}

export function fetchCaptcha(): Promise<CaptchaResult> {
  return unwrap<CaptchaResult>(http.post<CaptchaResult>('/auth/captcha', {}, { skipAuth: true }));
}

export async function login(payload: LoginPayload): Promise<LoginResultWithDeadline> {
  // 部署版后端（43d0a55）登录体字段是 account，⛔ username：发 username 会被记作缺账号失败并触发锁定；
  // remember 是纯前端偏好，不入请求体。
  const raw = await unwrap<BackendLoginData>(
    http.post<BackendLoginData>(
      '/auth/login',
      { account: payload.username, password: payload.password, remember: payload.remember },
      { skipAuth: true, silent: true },
    ),
  );
  return toLoginResult(raw);
}

/**
 * 部署版响应 → 内部 LoginResult（并带出**绝对**截止时刻 `sessionExpiresAtMs`）。
 * ⚠ 归一顺序＝① 体 `expiresAt` ② 令牌 `exp` ③ 体 `expireMinutes`；`expireMinutes` 仅用于
 * `sessionTimeoutMinutes` 展示兼容，⛔ 不作倒计时来源（倒计时只读绝对时刻）。
 */
export function toLoginResult(raw: BackendLoginData): LoginResultWithDeadline {
  if (!raw || typeof raw.token !== 'string' || !raw.token) {
    throw new ApiError('登录未取得登录凭证，请重试；若反复出现请联系平台管理员', 'AUTH_TOKEN_MISSING');
  }
  const claims = parseJwtClaims(raw.token);
  const user = raw.user ?? (claims ? userFromJwtClaims(claims) : null);
  if (!user) {
    // §6.2-7 未就绪且令牌无可解析声明：无法建立权限快照，明确失败而非静默放行
    throw new ApiError('登录响应未含身份快照，且登录令牌中没有可解析的身份声明，请重新登录或联系系统管理员', 'AUTH_PROFILE_MISSING');
  }
  const nominalMinutes = Number(raw.expireMinutes) > 0 ? Number(raw.expireMinutes) : 0;
  const declaredAtMs = raw.expiresAt ? new Date(raw.expiresAt).getTime() : 0;
  const claimExpMs = claimsExpiresAt(claims) ?? 0;
  const absoluteMs =
    declaredAtMs > 0 ? declaredAtMs : claimExpMs > 0 ? claimExpMs : nominalMinutes > 0 ? Date.now() + nominalMinutes * 60_000 : 0;
  if (absoluteMs <= 0) {
    throw new ApiError(
      '登录响应未给出会话绝对截止时刻（expiresAt／令牌 exp／expireMinutes 三者皆缺），拒绝以本地常量兜底',
      'AUTH_SESSION_DEADLINE_MISSING',
    );
  }
  return {
    accessToken: raw.token,
    refreshToken: undefined,
    // 由绝对时刻反推剩余秒数：单一来源（后端时刻），随取随算，⛔ 不自造时长
    expiresIn: Math.max(0, Math.floor((absoluteMs - Date.now()) / 1000)),
    sessionTimeoutMinutes: nominalMinutes,
    sessionExpiresAtMs: absoluteMs,
    user,
  };
}

export function logout(): Promise<void> {
  // 新契约要求请求体携带 `{ token }` 以便服务端精确失效化
  const token = localStorage.getItem('token') ?? '';
  return unwrap<void>(http.post<void>('/auth/logout', { token }));
}

/** 新契约端点：通过用户名＋手机号发起密码重置 */
export function resetPassword(username: string, phone: string): Promise<{ success: boolean; message: string }> {
  return unwrap<{ success: boolean; message: string }>(http.post<{ success: boolean; message: string }>('/auth/reset-password', { username, phone }, { skipAuth: true }));
}

/**
 * §3.5-② / §6.2-7：刷新页面后必须调本端点重建权限态，**不得**沿用 localStorage 陈旧快照
 * （权限可能已被 `role.permVersion` 变更）。后端把权限拆成菜单级 + 按钮级两份清单，
 * 由 `userFromProfileSnapshot` 归一为前端扁平 `permissions`。
 *
 * 兼容三种实际落地形状：① §6.2-7 `ProfileSnapshot`（联调真值）；② 内部 `CurrentUser`
 * （mock/桩直接给扁平快照）；③ 登录态 `{token,…}`（部分实现复用登录响应体）。
 */
export async function fetchProfile(): Promise<CurrentUser> {
  const raw = await unwrap<ProfileSnapshot | CurrentUser | BackendLoginData>(http.get<ProfileSnapshot>('/auth/profile'));

  const snap = raw as ProfileSnapshot;
  if (snap && typeof snap === 'object' && snap.account && Array.isArray(snap.buttonPermissions)) {
    return userFromProfileSnapshot(snap);
  }

  const flat = raw as CurrentUser;
  if (flat && typeof flat === 'object' && flat.account && Array.isArray(flat.permissions)) return flat;

  const login = raw as BackendLoginData;
  if (login && typeof login === 'object' && login.token) return toLoginResult(login).user;

  throw new ApiError(
    '登录用户信息不完整：缺少账号或权限清单',
    'PROFILE_SCHEMA_MISMATCH',
  );
}

/**
 * 自助改密：当前登录用户换掉自己的登录口令。
 * 成功时服务端回一句提示语（`data` 是字符串），失败按业务码区分「当前密码不对」与「新密码不合规」；
 * 三枚新密码输入框的预检在 `core/utils/passwordChange.ts`，本函数只负责发请求。
 * 路径字面量用裸形：前缀由 http 客户端的 base 提供一次，⛔ 在这里再补一段。
 */
export function changePassword(oldPassword: string, newPassword: string): Promise<string> {
  return unwrap<string>(
    http.post<string>('/auth/password/change', { oldPassword, newPassword }, { silent: true }),
  );
}
